import { http } from '@/api/client/http';
import {
  CanonicalProductListResponseSchema,
  CanonicalProductResponseSchema,
  OfferListResponseSchema,
} from '@/api/schemas/catalog';
import type { CanonicalProduct, Offer } from '@/api/schemas/catalog';

/**
 * Catalog module service — every API call this module makes passes through here.
 *
 * TWO READS, TWO AUTHORITIES
 * ----------------------------
 * The catalogue (M66) and the offer (M13) are separate resources with separate
 * scopes, so they are separate calls with separate outcomes:
 *
 *   - `listProducts` is a PUBLIC-scope read in M66. Everyone signed in sees it.
 *   - `listOffers` is a TENANT-scope read and needs `catalog.offer.read`. A
 *     viewer is refused with a `403`, and the page renders that refusal as a
 *     statement about the actor's role — never as an empty list, which would be
 *     indistinguishable from a tenant that sells nothing.
 */
export const CatalogApi = {
  /** GET /catalog/canonical-products — the shared catalogue. */
  listProducts: async (): Promise<CanonicalProduct[]> => {
    const response = await http.get('/catalog/canonical-products', CanonicalProductListResponseSchema);
    return response.data;
  },

  /** GET /catalog/canonical-products/{id} — one catalogue entry with its variants. */
  getProduct: async (id: string): Promise<CanonicalProduct> => {
    const response = await http.get(`/catalog/canonical-products/${id}`, CanonicalProductResponseSchema);
    return response.data;
  },

  /**
   * GET /catalog/offers — the CALLER'S TENANT's sellable offers.
   *
   * Rejects with an `ApiError` whose status is 403 when the caller holds no
   * `catalog.offer.read`. The caller decides how to present that; this service
   * does not swallow it.
   */
  listOffers: async (): Promise<Offer[]> => {
    const response = await http.get('/catalog/offers', OfferListResponseSchema);
    return response.data;
  },
};

/**
 * Groups a tenant's offers by the catalogue variant they sell.
 *
 * The join key is M13's `canonicalVariantId` — the offer names the catalogue
 * variant it sells. Grouping client-side keeps the two authorities apart: the
 * catalogue is read even when the offers are refused, so a viewer still sees
 * what the tenant sells without seeing the price.
 */
export function groupOffersByVariant(offers: ReadonlyArray<Offer>): Map<string, Offer[]> {
  const grouped = new Map<string, Offer[]>();

  for (const offer of offers) {
    const bucket = grouped.get(offer.canonicalVariantId);
    if (bucket) bucket.push(offer);
    else grouped.set(offer.canonicalVariantId, [offer]);
  }

  return grouped;
}
